import {
  LogLevel,
  LogCategory,
  LoggerConfig,
  CategoryConfig,
  LogEntry,
  LogHandler,
  TimerInfo,
  AssertionFailure,
  AssertionHandler,
  LogGroupScope,
  LogTimerScope,
  DEFAULT_CATEGORY_COLORS,
  LOG_LEVEL_COLORS,
  LOG_LEVEL_NAMES,
  BuiltinLogCategory,
} from './types';
import {
  devToolsDetector,
  supportsConsoleStyling,
  supportsConsoleGrouping,
  supportsConsoleTable,
  supportsConsoleTiming,
} from './DevToolsDetector';

/**
 * Default logger configuration
 */
const DEFAULT_CONFIG: LoggerConfig = {
  level: LogLevel.Debug,
  enabled: true,
  showTimestamp: true,
  showCategory: true,
  showLevel: true,
  useDevTools: true,
  useStyles: true,
  stackTraceOnError: true,
  stackTraceOnWarn: false,
  rateLimitMs: 0,
  categories: {},
};

/**
 * Message deduplication tracker for rate limiting
 */
interface RateLimitEntry {
  count: number;
  lastTime: number;
  suppressedCount: number;
}

/**
 * Main Logger class for game engine logging
 *
 * Features:
 * - Multiple log levels (Trace, Debug, Info, Warn, Error, Fatal)
 * - Category-based filtering (Render, Physics, Audio, etc.)
 * - DevTools integration (groups, tables, styling, timers)
 * - Rate limiting for duplicate messages
 * - Stack traces for errors
 * - Custom handlers for log forwarding
 *
 * @example
 * ```typescript
 * const log = Logger.create('Physics');
 * log.info('Simulation started');
 * log.debug('Bodies:', bodyCount);
 * log.warn('High velocity detected', velocity);
 * log.error('Collision solver failed', error);
 * ```
 */
export class Logger {
  private static _globalConfig: LoggerConfig = { ...DEFAULT_CONFIG };
  private static _handlers: Set<LogHandler> = new Set();
  private static _timers: Map<string, TimerInfo> = new Map();
  private static _rateLimitMap: Map<string, RateLimitEntry> = new Map();
  private static _loggers: Map<string, Logger> = new Map();
  private static _devToolsMode = false;
  private static _assertCount = 0;
  private static _assertionHandler: AssertionHandler | null = null;

  private _category: LogCategory;
  private _categoryColor: string;

  private constructor(category: LogCategory) {
    this._category = category;
    this._categoryColor =
      DEFAULT_CATEGORY_COLORS[category as BuiltinLogCategory] ||
      Logger.generateColor(category);
  }

  // ============== Static Factory Methods ==============

  /**
   * Create or get a logger instance for a category
   */
  static create(category: LogCategory): Logger {
    let logger = this._loggers.get(category);
    if (!logger) {
      logger = new Logger(category);
      this._loggers.set(category, logger);
    }
    return logger;
  }

  /**
   * Get the global 'Engine' logger
   */
  static get engine(): Logger {
    return this.create('Engine');
  }

  // ============== Configuration ==============

  /**
   * Get current global configuration
   */
  static get config(): Readonly<LoggerConfig> {
    return this._globalConfig;
  }

  /**
   * Update global configuration
   */
  static configure(config: Partial<LoggerConfig>): void {
    this._globalConfig = { ...this._globalConfig, ...config };
  }

  /**
   * Set global minimum log level
   */
  static setLevel(level: LogLevel): void {
    this._globalConfig.level = level;
  }

  /**
   * Enable or disable all logging
   */
  static setEnabled(enabled: boolean): void {
    this._globalConfig.enabled = enabled;
  }

  /**
   * Configure a specific category
   */
  static configureCategory(
    category: LogCategory,
    config: Partial<CategoryConfig>
  ): void {
    const existing = this._globalConfig.categories[category] || {
      enabled: true,
    };
    this._globalConfig.categories[category] = { ...existing, ...config };
  }

  /**
   * Enable a category
   */
  static enableCategory(category: LogCategory): void {
    this.configureCategory(category, { enabled: true });
  }

  /**
   * Disable a category
   */
  static disableCategory(category: LogCategory): void {
    this.configureCategory(category, { enabled: false });
  }

  /**
   * Set level for a specific category
   */
  static setCategoryLevel(category: LogCategory, level: LogLevel): void {
    this.configureCategory(category, { level });
  }

  /**
   * Enable multiple categories at once
   */
  static enableCategories(...categories: LogCategory[]): void {
    categories.forEach((cat) => this.enableCategory(cat));
  }

  /**
   * Disable multiple categories at once
   */
  static disableCategories(...categories: LogCategory[]): void {
    categories.forEach((cat) => this.disableCategory(cat));
  }

  /**
   * Enable only specified categories (disable all others)
   */
  static enableOnlyCategories(...categories: LogCategory[]): void {
    // Disable all
    for (const key of Object.keys(this._globalConfig.categories)) {
      this._globalConfig.categories[key as LogCategory] = {
        ...this._globalConfig.categories[key as LogCategory],
        enabled: false,
      };
    }
    // Enable specified
    categories.forEach((cat) => this.enableCategory(cat));
  }

  /**
   * Reset configuration to defaults
   */
  static resetConfig(): void {
    this._globalConfig = { ...DEFAULT_CONFIG };
  }

  /**
   * Check if DevTools features should be used
   */
  static get useDevToolsFeatures(): boolean {
    return (
      this._globalConfig.useDevTools &&
      (this._devToolsMode || devToolsDetector.isOpen)
    );
  }

  /**
   * Manually set DevTools mode (for testing or manual override)
   */
  static setDevToolsMode(enabled: boolean): void {
    this._devToolsMode = enabled;
  }

  // ============== Custom Handlers ==============

  /**
   * Add a custom log handler (e.g., for file logging, remote logging)
   */
  static addHandler(handler: LogHandler): void {
    this._handlers.add(handler);
  }

  /**
   * Remove a custom log handler
   */
  static removeHandler(handler: LogHandler): void {
    this._handlers.delete(handler);
  }

  /**
   * Clear all custom handlers
   */
  static clearHandlers(): void {
    this._handlers.clear();
  }

  // ============== Assertion Handler ==============

  /**
   * Set a callback for assertion failures
   * @param handler Function to call when an assertion fails, or null to remove
   */
  static onAssertionFailed(handler: AssertionHandler | null): void {
    this._assertionHandler = handler;
  }

  /**
   * Get the current assertion handler
   */
  static getAssertionHandler(): AssertionHandler | null {
    return this._assertionHandler;
  }

  // ============== Utility Methods ==============

  /**
   * Generate a consistent color for custom categories
   */
  private static generateColor(category: string): string {
    let hash = 0;
    for (let i = 0; i < category.length; i++) {
      hash = category.charCodeAt(i) + ((hash << 5) - hash);
    }
    const h = hash % 360;
    return `hsl(${h}, 70%, 50%)`;
  }

  /**
   * Get rate limit key for a message
   */
  private static getRateLimitKey(
    category: LogCategory,
    level: LogLevel,
    message: string
  ): string {
    return `${category}:${level}:${message}`;
  }

  /**
   * Check if message should be rate limited
   */
  private static checkRateLimit(
    category: LogCategory,
    level: LogLevel,
    message: string
  ): boolean {
    if (this._globalConfig.rateLimitMs <= 0) return false;

    const key = this.getRateLimitKey(category, level, message);
    const now = Date.now();
    const entry = this._rateLimitMap.get(key);

    if (!entry) {
      this._rateLimitMap.set(key, {
        count: 1,
        lastTime: now,
        suppressedCount: 0,
      });
      return false;
    }

    const elapsed = now - entry.lastTime;
    if (elapsed >= this._globalConfig.rateLimitMs) {
      // Reset and allow
      if (entry.suppressedCount > 0) {
        console.log(
          `[${category}] (${entry.suppressedCount} duplicate messages suppressed)`
        );
      }
      entry.count = 1;
      entry.lastTime = now;
      entry.suppressedCount = 0;
      return false;
    }

    // Rate limit
    entry.suppressedCount++;
    return true;
  }

  /**
   * Clear rate limit tracking
   */
  static clearRateLimits(): void {
    this._rateLimitMap.clear();
  }

  // ============== Instance Methods ==============

  /**
   * Get the category name for this logger
   */
  get category(): LogCategory {
    return this._category;
  }

  /**
   * Check if logging is enabled for this logger at the given level
   */
  isEnabled(level: LogLevel): boolean {
    const config = Logger._globalConfig;
    if (!config.enabled) return false;

    const categoryConfig = config.categories[this._category];
    if (categoryConfig?.enabled === false) return false;

    const effectiveLevel = categoryConfig?.level ?? config.level;
    return level >= effectiveLevel;
  }

  /**
   * Create a child logger with a sub-category
   */
  child(subCategory: string): Logger {
    return Logger.create(`${this._category}/${subCategory}` as LogCategory);
  }

  // ============== Logging Methods ==============

  /**
   * Log at TRACE level
   */
  trace(message: string, ...args: unknown[]): void {
    this.log(LogLevel.Trace, message, args);
  }

  /**
   * Log at DEBUG level
   */
  debug(message: string, ...args: unknown[]): void {
    this.log(LogLevel.Debug, message, args);
  }

  /**
   * Log at INFO level
   */
  info(message: string, ...args: unknown[]): void {
    this.log(LogLevel.Info, message, args);
  }

  /**
   * Log at WARN level
   */
  warn(message: string, ...args: unknown[]): void {
    this.log(LogLevel.Warn, message, args);
  }

  /**
   * Log at ERROR level
   */
  error(message: string, ...args: unknown[]): void {
    this.log(LogLevel.Error, message, args);
  }

  /**
   * Log at FATAL level
   */
  fatal(message: string, ...args: unknown[]): void {
    this.log(LogLevel.Fatal, message, args);
  }

  /**
   * Core logging method
   */
  private log(level: LogLevel, message: string, args: unknown[]): void {
    if (!this.isEnabled(level)) return;

    // Rate limiting
    if (Logger.checkRateLimit(this._category, level, message)) return;

    const entry: LogEntry = {
      level,
      category: this._category,
      message,
      args,
      timestamp: Date.now(),
    };

    // Add stack trace for errors/warnings if configured
    if (
      (level >= LogLevel.Error && Logger._globalConfig.stackTraceOnError) ||
      (level === LogLevel.Warn && Logger._globalConfig.stackTraceOnWarn)
    ) {
      entry.stackTrace = new Error().stack;
    }

    // Output to console
    this.outputToConsole(entry);

    // Forward to custom handlers
    Logger._handlers.forEach((handler) => {
      try {
        handler.handle(entry);
      } catch (e) {
        // Ignore handler errors to prevent infinite loops
      }
    });
  }

  /**
   * Output log entry to console with DevTools enhancements
   */
  private outputToConsole(entry: LogEntry): void {
    const config = Logger._globalConfig;
    const useDevTools = Logger.useDevToolsFeatures;
    const useStyles = config.useStyles && supportsConsoleStyling();

    // Build prefix parts (without message to avoid %c conflicts)
    const parts: string[] = [];
    const styles: string[] = [];

    if (config.showTimestamp) {
      const time = new Date(entry.timestamp).toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      if (useStyles) {
        parts.push(`%c${time}`);
        styles.push('color: #6B7280');
      } else {
        parts.push(`[${time}]`);
      }
    }

    if (config.showLevel) {
      const levelName = LOG_LEVEL_NAMES[entry.level];
      if (useStyles) {
        parts.push(`%c${levelName.padEnd(5)}`);
        styles.push(
          `color: ${LOG_LEVEL_COLORS[entry.level]}; font-weight: bold`
        );
      } else {
        parts.push(`[${levelName}]`);
      }
    }

    if (config.showCategory) {
      const categoryColor =
        config.categories[this._category]?.color || this._categoryColor;
      if (useStyles) {
        parts.push(`%c[${entry.category}]`);
        styles.push(
          `color: ${categoryColor}; font-weight: 500`
        );
      } else {
        parts.push(`[${entry.category}]`);
      }
    }

    // Select console method based on level
    const consoleMethod = this.getConsoleMethod(entry.level);

    // Output: message and args go after styles to avoid % interpretation issues
    if (useStyles) {
      consoleMethod(parts.join(' '), ...styles, entry.message, ...entry.args);
    } else {
      consoleMethod(parts.join(' '), entry.message, ...entry.args);
    }

    // Output stack trace if available
    if (entry.stackTrace && useDevTools) {
      console.groupCollapsed('%cStack Trace', 'color: #9CA3AF; font-size: 10px');
      console.log(entry.stackTrace);
      console.groupEnd();
    }
  }

  /**
   * Get appropriate console method for log level
   */
  private getConsoleMethod(
    level: LogLevel
  ): (...data: unknown[]) => void {
    switch (level) {
      case LogLevel.Trace:
      case LogLevel.Debug:
        return (...data: unknown[]) => console.debug(...data);
      case LogLevel.Info:
        return (...data: unknown[]) => console.info(...data);
      case LogLevel.Warn:
        return (...data: unknown[]) => console.warn(...data);
      case LogLevel.Error:
      case LogLevel.Fatal:
        return (...data: unknown[]) => console.error(...data);
      default:
        return (...data: unknown[]) => console.log(...data);
    }
  }

  // ============== DevTools Enhanced Methods ==============

  /**
   * Log a table (uses console.table in DevTools)
   */
  table(message: string, data: object | unknown[]): void {
    if (!this.isEnabled(LogLevel.Debug)) return;

    if (Logger.useDevToolsFeatures && supportsConsoleTable()) {
      this.info(message);
      console.table(data);
    } else {
      this.info(message, data);
    }
  }

  /**
   * Start a collapsed group and return a disposable scope
   * @example
   * ```typescript
   * // With using keyword (auto-close)
   * using _ = log.group('Update');
   * log.debug('step 1');
   * log.debug('step 2');
   * // Group automatically closed when scope ends
   * 
   * // Manual control
   * const scope = log.group('Render');
   * log.debug('drawing...');
   * scope.end();
   * ```
   */
  group(label: string, collapsed = true): LogGroupScope {
    const enabled = this.isEnabled(LogLevel.Debug);
    let ended = false;

    if (enabled) {
      if (Logger.useDevToolsFeatures && supportsConsoleGrouping()) {
        const prefix = `[${this._category}] ${label}`;
        if (collapsed) {
          console.groupCollapsed(prefix);
        } else {
          console.group(prefix);
        }
      } else {
        this.debug(`▸ ${label}`);
      }
    }

    const end = () => {
      if (ended) return;
      ended = true;
      if (enabled && Logger.useDevToolsFeatures && supportsConsoleGrouping()) {
        console.groupEnd();
      }
    };

    return {
      end,
      [Symbol.dispose]: end,
    };
  }

  /**
   * End a group (legacy method, prefer using Disposable pattern)
   * @deprecated Use `using _ = log.group('label')` instead
   */
  groupEnd(): void {
    if (Logger.useDevToolsFeatures && supportsConsoleGrouping()) {
      console.groupEnd();
    }
  }

  /**
   * Log with automatic grouping (useful for complex objects)
   * @returns The return value of the callback function
   */
  grouped<T>(label: string, fn: () => T): T {
    using _ = this.group(label, true);
    return fn();
  }

  /**
   * Async version of grouped logging
   */
  async groupedAsync<T>(label: string, fn: () => Promise<T>): Promise<T> {
    using _ = this.group(label, true);
    return await fn();
  }

  /**
   * Start a timer (legacy method without return)
   */
  time(label: string): void {
    const fullLabel = `${this._category}:${label}`;
    
    Logger._timers.set(fullLabel, {
      label,
      startTime: performance.now(),
      category: this._category,
    });

    if (Logger.useDevToolsFeatures && supportsConsoleTiming()) {
      console.time(fullLabel);
    }
  }

  /**
   * End a timer and log the duration
   */
  timeEnd(label: string): number {
    const fullLabel = `${this._category}:${label}`;
    const timer = Logger._timers.get(fullLabel);

    if (!timer) {
      this.warn(`Timer "${label}" does not exist`);
      return 0;
    }

    const duration = performance.now() - timer.startTime;
    Logger._timers.delete(fullLabel);

    if (Logger.useDevToolsFeatures && supportsConsoleTiming()) {
      console.timeEnd(fullLabel);
    } else {
      this.debug(`${label}: ${duration.toFixed(2)}ms`);
    }

    return duration;
  }

  /**
   * Start a timer and return a disposable scope
   * @example
   * ```typescript
   * // With using keyword (auto-stop)
   * using timer = log.startTimer('Load Assets');
   * await loadAssets();
   * // Timer automatically stopped when scope ends
   * 
   * // Manual control
   * const timer = log.startTimer('Process');
   * // ... do work
   * console.log('Elapsed so far:', timer.elapsed());
   * const duration = timer.stop();
   * ```
   */
  startTimer(label: string): LogTimerScope {
    const fullLabel = `${this._category}:${label}`;
    const startTime = performance.now();
    let stopped = false;

    Logger._timers.set(fullLabel, {
      label,
      startTime,
      category: this._category,
    });

    if (Logger.useDevToolsFeatures && supportsConsoleTiming()) {
      console.time(fullLabel);
    }

    const elapsed = (): number => {
      return performance.now() - startTime;
    };

    const stop = (): number => {
      if (stopped) return 0;
      stopped = true;

      const duration = performance.now() - startTime;
      Logger._timers.delete(fullLabel);

      if (Logger.useDevToolsFeatures && supportsConsoleTiming()) {
        console.timeEnd(fullLabel);
      } else {
        this.debug(`${label}: ${duration.toFixed(2)}ms`);
      }

      return duration;
    };

    return {
      elapsed,
      stop,
      [Symbol.dispose]: stop,
    };
  }

  /**
   * Log with timing (measures execution time of a function)
   */
  timed<T>(label: string, fn: () => T): T {
    using timer = this.startTimer(label);
    return fn();
  }

  /**
   * Async version of timed logging
   */
  async timedAsync<T>(label: string, fn: () => Promise<T>): Promise<T> {
    using timer = this.startTimer(label);
    return await fn();
  }

  /**
   * Log a stack trace
   */
  stack(message?: string): void {
    if (!this.isEnabled(LogLevel.Debug)) return;

    if (message) {
      this.debug(message);
    }
    console.trace();
  }

  /**
   * Conditional logging (assert-like, logs only if condition is false)
   * If an assertion handler is set via Logger.onAssertionFailed(), it will be called.
   * @returns false if assertion failed, true if passed
   */
  assert(condition: unknown, message: string, ...args: unknown[]): boolean {
    if (!condition) {
      Logger._assertCount++;
      
      const failure: AssertionFailure = {
        category: this._category,
        message,
        args,
        timestamp: Date.now(),
        stackTrace: new Error().stack,
      };

      // Call assertion handler if set (may throw)
      Logger._assertionHandler?.(failure);

      this.error(`Assertion failed: ${message}`, ...args);
      if (Logger.useDevToolsFeatures) {
        console.trace('Assertion call stack');
      }
      return false;
    }
    return true;
  }

  /**
   * Log only once (useful for one-time warnings)
   */
  private static _onceKeys = new Set<string>();
  
  once(message: string, ...args: unknown[]): void {
    const key = `${this._category}:${message}`;
    if (Logger._onceKeys.has(key)) return;
    Logger._onceKeys.add(key);
    this.warn(message, ...args);
  }

  /**
   * Clear "once" tracking
   */
  static clearOnce(): void {
    this._onceKeys.clear();
  }

  /**
   * Log an object with pretty formatting
   */
  dir(label: string, obj: object): void {
    if (!this.isEnabled(LogLevel.Debug)) return;

    if (Logger.useDevToolsFeatures) {
      this.debug(label);
      console.dir(obj);
    } else {
      this.debug(label, JSON.stringify(obj, null, 2));
    }
  }

  /**
   * Clear console
   */
  static clear(): void {
    console.clear();
    this._assertCount = 0;
  }

  /**
   * Get assertion count since last clear
   */
  static get assertCount(): number {
    return this._assertCount;
  }

  // ============== Convenience Shortcuts ==============

  /**
   * Quick formatted log with object inspection
   */
  inspect(label: string, value: unknown): void {
    if (!this.isEnabled(LogLevel.Debug)) return;

    const type = typeof value;
    const isObject = value !== null && type === 'object';
    
    if (isObject && Logger.useDevToolsFeatures) {
      this.grouped(label, () => {
        if (Array.isArray(value)) {
          console.table(value);
        } else {
          console.dir(value);
        }
      });
    } else {
      this.debug(`${label}:`, value);
    }
  }

  /**
   * Log performance metrics
   */
  perf(label: string, metrics: Record<string, number>): void {
    if (!this.isEnabled(LogLevel.Debug)) return;

    if (Logger.useDevToolsFeatures && supportsConsoleTable()) {
      this.info(`⚡ ${label}`);
      console.table(metrics);
    } else {
      const metricsStr = Object.entries(metrics)
        .map(([k, v]) => `${k}: ${v.toFixed(2)}`)
        .join(', ');
      this.info(`⚡ ${label} - ${metricsStr}`);
    }
  }
}

// ============== Convenience Exports ==============

/**
 * Pre-configured loggers for common categories
 */
export const Log = {
  Engine: Logger.create('Engine'),
  Render: Logger.create('Render'),
  Physics: Logger.create('Physics'),
  Audio: Logger.create('Audio'),
  Input: Logger.create('Input'),
  Network: Logger.create('Network'),
  AI: Logger.create('AI'),
  UI: Logger.create('UI'),
  Script: Logger.create('Script'),
  Asset: Logger.create('Asset'),
  Scene: Logger.create('Scene'),
  Editor: Logger.create('Editor'),
  VFX: Logger.create('VFX'),
  Animation: Logger.create('Animation'),
  Memory: Logger.create('Memory'),
} as const;
